import { createSelector } from '@reduxjs/toolkit';
import { partition } from 'lodash';
import get from 'lodash/get';

import { GET_RECEIPT_RESPONSE } from 'actions/baskets';
import {
  GET_USER_BEHAVIORAL_DISCOUNTS_RESPONSE,
  CLAIM_USER_BEHAVIORAL_DISCOUNT_RESPONSE,
  CLEAR_USER_BEHAVIORAL_DISCOUNT_CLAIM,
} from 'lib/constants/v3Actions';

const INITIAL_USER_BEHAVIORAL_DISCOUNTS = [];

const initialState = { isLoading: true, confirmationDetails: null };

const addUserBehavioralDiscountsForMenu = (state, menuSlug, userBehavioralDiscounts) => {
  return {
    ...state,
    isLoading: false,
    [menuSlug]: userBehavioralDiscounts,
  };
};

const shouldAddUserBehavioralDiscounts = action => {
  return action.response && action.response.behavioralDiscounts;
};

const mergeUserBehavioralDiscounts = (userBehavioralDiscounts, updatedUserBehavioralDiscounts) => {
  const updatedUserBehavioralDiscountsById = Object.fromEntries(
    updatedUserBehavioralDiscounts.map(updatedUserBehavioralDiscount => [
      updatedUserBehavioralDiscount.userBehavioralDiscountId,
      updatedUserBehavioralDiscount,
    ])
  );

  return userBehavioralDiscounts.map(userBehavioralDiscount =>
    updatedUserBehavioralDiscountsById[userBehavioralDiscount.id]
      ? {
          ...userBehavioralDiscount,
          ...updatedUserBehavioralDiscountsById[userBehavioralDiscount.id],
        }
      : userBehavioralDiscount
  );
};

export default (state = initialState, action) => {
  switch (action.type) {
    case GET_USER_BEHAVIORAL_DISCOUNTS_RESPONSE:
      return addUserBehavioralDiscountsForMenu(
        state,
        action.response.menuSlug,
        action.response.userBehavioralDiscounts
      );
    case GET_RECEIPT_RESPONSE: {
      if (shouldAddUserBehavioralDiscounts(action)) {
        const userBehavioralDiscounts = selectUserBehavioralDiscountsForMenu(
          state,
          action.params?.menuSlug
        );

        return addUserBehavioralDiscountsForMenu(
          state,
          action.params?.menuSlug,
          mergeUserBehavioralDiscounts(userBehavioralDiscounts, action.response.behavioralDiscounts)
        );
      }
      return state;
    }
    case CLAIM_USER_BEHAVIORAL_DISCOUNT_RESPONSE: {
      const userBehavioralDiscounts = selectUserBehavioralDiscountsForMenu(
        state,
        action.params?.menuSlug
      );

      return {
        ...addUserBehavioralDiscountsForMenu(
          state,
          action.params?.menuSlug,
          mergeUserBehavioralDiscounts(
            userBehavioralDiscounts,
            action.response.userBehavioralDiscounts
          )
        ),
        confirmationDetails: action.response.confirmationDetails ?? null,
      };
    }
    case CLEAR_USER_BEHAVIORAL_DISCOUNT_CLAIM:
      return { ...state, confirmationDetails: null };
    default:
      return state;
  }
};

export const selectUserBehavioralDiscountsForMenu = (state, menuSlug) =>
  get(state, menuSlug, INITIAL_USER_BEHAVIORAL_DISCOUNTS);

export const selectUserBehavioralDiscountsForPromotionalDisplay = createSelector(
  [(state, menuSlug) => selectUserBehavioralDiscountsForMenu(state, menuSlug)],
  userBehavioralDiscounts =>
    userBehavioralDiscounts.filter(
      userBehavioralDiscount => userBehavioralDiscount.shouldDisplayBanner
    )
);
export const selectUserBehavioralDiscountsIsLoading = state => state.isLoading;

export const selectClaimedUserBehavioralDiscountIdForMenu = createSelector(
  [(state, menuSlug) => selectUserBehavioralDiscountsForMenu(state, menuSlug)],
  userBehavioralDiscounts => {
    const claimedDiscount = userBehavioralDiscounts.find(
      userBehavioralDiscount => userBehavioralDiscount.isClaimed
    );
    return claimedDiscount ? claimedDiscount.id : null;
  }
);

export const partitionUserBehavioralDiscountsForPromotionalDisplay = (state, menuSlug) => {
  return partition(
    selectUserBehavioralDiscountsForPromotionalDisplay(state, menuSlug),
    ubd => ubd.requiresClaim
  );
};

export const selectConfirmationDetails = state => state.confirmationDetails ?? null;
